<template>
  <div v-if="bugReport" class="app-modal__box">
    <div class="app-modal__header">
      <h3 class="text-primary font-semibold mb-3">
        {{ $t("We're here to improve") }}
      </h3>
      <p class="!mb-0 text-[#4D4D4D]">
        <span class="font-semibold">{{ $t('Please note:') }}</span>
        {{
          $t(
            "This report helps us fix bugs. We won't reply directly to this submission."
          )
        }}
      </p>
    </div>
    <div class="app-modal__body">
      <div v-if="showThanks" class="flex items-center justify-center">
        <div class="flex items-center">
          <span class="material-symbols-outlined text-successful text-2xl mr-2"
            >check_circle</span
          >
          <h3 class="!mb-0 text-primary font-semibold py-12">
            {{ $t('Thank you!') }}
          </h3>
        </div>
      </div>
      <BaseTextarea
        v-else
        :placeholder="'Tell us what went wrong and what you expected to happen.'"
        :value="message"
        @input="message = $event.target.value"
      >
      </BaseTextarea>
      <div class="mt-4 flex items-center justify-between text-primary">
        <a @click="returnToMenu" class="flex gap-2 items-center">
          <i class="mdi mdi-arrow-left text-xl"></i>
          <div class="font-semibold text-base">
            {{ $t('Back to help menu') }}
          </div>
        </a>
        <BaseButton
          v-if="!showThanks"
          :label="'Send report'"
          @click="sentrySubmit()"
        >
        </BaseButton>
      </div>
    </div>
  </div>
  <div v-else class="app-modal__box">
    <div class="app-modal__header">
      <h3 class="text-primary font-semibold mb-3">
        {{ $t('Help and support') }}
      </h3>
      <p class="!mb-0 text-[#4D4D4D]">
        {{
          $t(
            'Our comprehensive support center is here to help you with any request. Select below where you want to go.'
          )
        }}
      </p>
    </div>
    <div class="app-modal__body">
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div
          v-for="item in options"
          class="relative flex items-center space-x-3 rounded-lg border border-gray-100 bg-white p-2 shadow-sm focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 hover:bg-gray-50 transition-all duration-300 ease-in-out"
        >
          <div class="min-w-0 flex-1">
            <div @click="item.onClick" class="focus:outline-none">
              <span class="absolute inset-0" aria-hidden="true" />
              <div class="flex items-center justify-between">
                <p class="!mb-1 text-sm font-semibold text-primary">
                  {{ item.title }}
                </p>
                <span
                  v-if="item.external"
                  class="material-symbols-outlined--small text-primary"
                  >open_in_new</span
                >
              </div>
              <p class="!mb-0 truncate text-xs text-secondaryG-700">
                {{ item.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
const { t } = useI18n()
const { userProfile } = useKeycloak()
const { $features, $sentry, $bus }: any = useNuxtApp()
const localePath = useLocalePath()

const props = withDefaults(
  defineProps<{
    isEditor?: boolean
  }>(),
  {
    isEditor: false,
  }
)

const message = ref('')
const bugReport = ref(false)
const showThanks = ref(false)

const options = computed(() => {
  let baseOptions = [
    {
      title: 'Help Center',
      description: 'Guides, Manuals, FAQ’s and more!',
      external: true,
      onClick: () => window.open('https://help.transkribus.org/', '_blank'),
    },
    {
      title: 'Request Support',
      description: 'Send us your support request',
      external: true,
      onClick: () =>
        window.open('https://help.transkribus.org/kb-tickets/new', '_blank'),
    },
    {
      title: 'Release Notes',
      description: 'New features and improvements',
      external: false,
      onClick: () => window.open(localePath({ name: 'changelog' }), '_blank'),
    },
    {
      title: 'Report a bug',
      description: 'Document a bug to improve Transkribus',
      external: false,
      onClick: () => (bugReport.value = true),
    },
  ]
  if (!props.isEditor) {
    baseOptions.push({
      title: 'Restart onboarding tour',
      description: 'Go through our onboarding tour again',
      external: false,
      onClick: () => emitOnBoarding(),
    })
  }
  return baseOptions
})

onMounted(async () => {
  if (userProfile?.Email) {
    $sentry.setUser({
      email: userProfile.Email,
    })
  }
})

function sentrySubmit() {
  $sentry.sendFeedback({
    email: String(userProfile?.Email),
    message: String(message.value),
  })
  message.value = ''

  showThanks.value = true
}

function returnToMenu() {
  bugReport.value = false
  message.value = ''

  showThanks.value = false
}

function emitOnBoarding() {
  $bus.emit('close-modal')
  $bus.emit('startOnboarding')
}
</script>
